// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Badge,
} from 'antd';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import SimpleReactValidator from 'simple-react-validator';
import {
  SpButton,
  FilterWrapper,
  SpText,
  SpStatusTag,
} from 'components/DesignKit';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import getFilterOperators from 'utils/getFilterOperators';
import getEventsBackground from 'utils/getEventsBackground';
import getEventsStatus from 'utils/getEventsStatus';

import {
  eventPageActions, filterParamsActions, notificationActions, eventDetailsAction,
} from 'store/actions';
import { FILTERS_SPECS, NOTIFICATION } from 'store/actionTypes';
import {
  PAGES,
  ROLES,
  FILTERS_TYPES,
} from 'appconstants';

import moment from 'moment-timezone';
import RBAC from 'components/rbac';
import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import Clipboard from 'components/NavIcons/copyclipboard';
import DownloadQr from 'components/NavIcons/downloadqr';

import ShowQrCode from './components/ShowQrCode';


const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const EVENTS_FILTERS_TYPES = FILTERS_TYPES.EVENTS;

type Props = {
  events: Array<Object>,
  fetchEventPages: Function,
  selectedAccount: Object,
  totalElements: number,
  test: boolean,
  loading: boolean,
  history: {
    push: Function,
  },
  setFilterData: Function,
  preserveFilters: Object,
  setNotification: Function,
  submitting: Boolean,
  clearEventDetails: Function,
  isMobileView: Boolean,
};

const EventsList = (props: Props) => {
  const {
    selectedAccount,
    events,
    loading,
    fetchEventPages,
    totalElements,
    test,
    history,
    setFilterData,
    preserveFilters,
    setNotification,
    submitting,
    clearEventDetails,
    isMobileView,
  } = props;

  const env = process.env.REACT_APP_ENV;

  const tableId = document.getElementsByTagName('table');
  if (tableId && tableId[0] && isMobileView) {
    tableId[0].style.width = '1600px';
  } else if (tableId && tableId[0] && !isMobileView) {
    tableId[0].style.width = '100%';
  }

  const [, forceUpdate] = useState();
  const location = useLocation();
  const myRef = useRef();
  const { pathname } = location;
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filterTypes, setFilterTypes] = useState(EVENTS_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const simpleValidator = useRef(new SimpleReactValidator());
  const [filterEvents, setFilterEvents] = useState((preserveFilters && preserveFilters.filterEvents)
    ? (preserveFilters && preserveFilters.filterEvents)
    : [{
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState('');
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterEvents: [{
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        checked: true,
      }],
    });
  const isSeatedEvent = !pathname.includes('non-seated') && pathname.includes('seated');

  const debouncedFetchEvents = debounce(fetchEventPages, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const seated = `seated=${isSeatedEvent}`;
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchEvents({
      currentPage, sortParams, filterParams, seated,
    });
  };

  const exportData = events ? events.map((values) => ({
    title: values && values.title,
    totalSales: values && values.totalSales,
    createdAt: moment
      .utc(values && values.createdAt)
      .tz(selectedAccount && selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
    status: values && getEventsStatus(values.status),
  })) : '';

  const exportHeaders = [
    { label: 'TITLE', key: 'title' },
    { label: 'TOTAL ONLINE SALES', key: 'totalSales' },
    { label: 'CREATED ON', key: 'createdAt' },
    { label: 'STATUS', key: 'status' },
  ];

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      // inside click
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      // Outside click
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  useEffect(() => {
    getData(location.search);
  }, [location, test, selectedAccount]);


  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [events]);

  const copyCode = (record) => {
    let url;
    if (env === 'Production') {
      url = `https://secure.swirepay.com/event-page/${record.gid}`;
    } else if (env === 'Staging') {
      url = `https://staging-secure.swirepay.com/event-page/${record.gid}`;
    } else {
      url = `http://localhost:4000/event-page/${record.gid}`;
    }
    navigator.clipboard.writeText(url);
    document.execCommand('copy');
    setNotification({
      type: NOTIFICATION.SUCCESS,
      payload: 'Event url copied to clipboard!',
    });
  };

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.NON_SEATED_EVENTS,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.NON_SEATED_EVENTS,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const selectedRecord = (record) => {
    const eventsGid = record.gid;
    history.push({
      pathname: `${PAGES.EVENT_PAGES}/${eventsGid}`,
      state: location.search,
    });
  };

  const onCheck = (e, index) => {
    const list = [...filterEvents];
    if (filterEvents.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = EVENTS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setFilterEvents(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterEvents];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = EVENTS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setFilterEvents(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterEvents[index].filterName === 'createdAt') {
      if (filterEvents[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).endOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterEvents[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterEvents[index].filterOperator === 'GT') {
        const utcDate = moment(e).endOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterEvents];
    list[index].filterValue = selectedValue;
    setFilterEvents(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterEvents];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterEvents[index].filterValue;
        handleValue(value, index);
      }
    }
    setFilterEvents(list);
  };

  const handleAddClick = () => {
    const list = [...filterEvents];
    setFilterEvents([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setFilterEvents([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
    setFilterTypes(EVENTS_FILTERS_TYPES);
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.NON_SEATED_EVENTS);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    const formValid = simpleValidator.current.allValid();
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }

    const eventsFilters = filterEvents.filter((item) => item.checked);
    if (eventsFilters[0].filterValue) {
      setCount(eventsFilters && eventsFilters.length);
      for (let i = 0; i < eventsFilters.length; i += 1) {
        if (eventsFilters[i].checked) {
          if (eventsFilters[i].filterName === 'createdAt') {
            eventsFilters[i] = eventsFilters[i].filterValue;
          } else if (eventsFilters[i].filterName === 'email') {
            const email = encodeURIComponent(eventsFilters[i].filterValue);
            eventsFilters[i] = `${eventsFilters[i].filterName}.${eventsFilters[i].filterOperator}=${email}`;
          } else {
            eventsFilters[i] = `${eventsFilters[i].filterName}.${eventsFilters[i].filterOperator}=${eventsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(eventsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterEvents,
        });
        history.push({
          pathname: PAGES.NON_SEATED_EVENTS,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterEvents,
        });
        history.push({
          pathname: PAGES.NON_SEATED_EVENTS,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const selectedQrCode = (record) => {
    let url;
    if (env === 'Production') {
      url = `https://secure.swirepay.com/event-page/${record.gid}`;
    } else if (env === 'Staging') {
      url = `https://staging-secure.swirepay.com/event-page/${record.gid}`;
    } else {
      url = `http://localhost:4000/event-page/${record.gid}`;
    }
    setSelectedQR(url);
    setShowQrCodeModal(true);
  };

  const returnFromQrCode = (qrVlue) => {
    if (qrVlue === true) {
      setShowQrCodeModal(false);
    }
  };

  const columns = [
    {
      title: 'TITLE',
      dataIndex: 'title',
      width: '25%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (title) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {title || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'TOTAL SALES',
      dataIndex: 'totalOnlineSales',
      width: '20%',
      align: 'left',
      render: (totalOnlineSales, record) => {
        const { totalOfflineSales } = record;
        return (
          <Tooltip placement="top" title="Click here to view details">
            <span style={{ cursor: 'pointer' }}>
              {totalOnlineSales + totalOfflineSales || <>&#8211;</>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'CREATED ON',
      dataIndex: 'createdAt',
      width: '20%',
      align: 'left',
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '15%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (status) => (
        <SpStatusTag style={{ backgroundColor: getEventsBackground(status) }}>
          {getEventsStatus(status)}
        </SpStatusTag>
      ),
    },
  ];

  const actionColumn = {
    title: 'ACTION',
    dataIndex: 'action',
    width: '20%',
    align: 'center',
    render: (text, record) => {
      const { status } = record;
      if (status === 'ACTIVE') {
        return (
          <span>
            <SpButton
              type="secondary"
              borderRadius="50px"
              className="mr-2"
              style={{ cursor: 'pointer' }}
              disabled={record.status !== 'ACTIVE'}
              onClick={(e) => { e.stopPropagation(); selectedQrCode(record); }}
            >
              <DownloadQr />
            </SpButton>
            <SpButton
              type="secondary"
              borderRadius="50px"
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); copyCode(record); }}
              disabled={record.status !== 'ACTIVE'}
            >
              <Clipboard />
            </SpButton>
          </span>
        );
      }
      return (
        <span>
          <SpButton
            type="secondary"
            borderRadius="50px"
            disabled
          >
            <Clipboard />
          </SpButton>
        </span>
      );
    },
  };

  const AddSeatedEvent = () => {
    clearEventDetails(true);
    history.push(`${PAGES.ADDEVENTSPAGE}?seated=${isSeatedEvent}`);
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <RBAC
            permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]}
          >
            <Col>
              <SpButton
                type="secondary"
                shape="round"
                className="mr-3"
                ghost
                onClick={() => AddSeatedEvent()}
              >
                {'\u002B'}&nbsp;{isSeatedEvent ? 'Add Seated Event Page' : 'Add Non Seated Event Page'}
              </SpButton>
              <span>
                {
                  exportData !== null
                    ? (
                      <Tooltip placement="left" title="Click here to export Events">
                        <CSVLink
                          target="_blank"
                          className="mr-3"
                          filename="Events Report.csv"
                          data={exportData}
                          headers={exportHeaders}
                          onMouseEnter={() => setFillColor(true)}
                          onMouseLeave={() => setFillColor(false)}
                        >
                          <ExportIcon
                            fill={fillColor
                              ? '#279dfe'
                              : '#C0C0C0'}
                          />
                        </CSVLink>
                      </Tooltip>
                    ) : null
                }
              </span>
              <span ref={myRef}>
                <span
                  onClick={() => setOpen(!open)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setFilterColor(true)}
                  onMouseLeave={() => setFilterColor(false)}
                >
                  {open && (
                  <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                    <FilterIcon
                      fill={filterColor
                        ? '#279dfe'
                        : '#C0C0C0'}
                    />
                  </Badge>
                  )}
                  {!open && (
                  <Tooltip placement="left" title="Click here to filter Events">
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  </Tooltip>
                  )}
                </span>
                {open && (
                  <FilterWrapper>
                    <Filters
                      filtersGrid={filterEvents}
                      filterTypes={filterTypes}
                      handleChange={handleChange}
                      onCheck={onCheck}
                      fiterOperators={fiterOperators}
                      handleOperator={handleOperator}
                      handleValue={handleValue}
                      startDate={startDate}
                      endDate={endDate}
                      currentDate={currentDate}
                    />
                    <div className="px-4 mt-2">
                      {
                        filterEvents.length < EVENTS_FILTERS_TYPES.length && (
                          <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                            <SpText
                              fontWeight="600"
                              color="#279dfe"
                              onClick={handleAddClick}
                            >
                              {'\u002B'}&nbsp; Add another filter
                            </SpText>
                          </div>
                        )
                      }
                    </div>
                    <div className="d-flex mb-2 justify-content-end mr-3">
                      <SpButton
                        type="secondary"
                        shape="round"
                        className="mr-2"
                        ghost
                        onClick={resetFilters}
                      >
                        Clear
                      </SpButton>
                      <SpButton
                        type="primary"
                        shape="round"
                        onClick={applyFilters}
                      >
                        Save
                      </SpButton>
                    </div>
                  </FilterWrapper>
                )}
              </span>
            </Col>
          </RBAC>
        </div>
      </Row>
      {/* SHOW QR MODAL */}
      {showQrCodeModal && (
      <ShowQrCode
        visible={showQrCodeModal}
        submitting={submitting}
        selectedQR={selectedQR}
        returnFromQrCode={returnFromQrCode}
        close={() => setShowQrCodeModal(false)}
      />
      )}
      <RBAC
        fallback={(
          <Table
            onRow={(record) => ({
              onClick: () => {
                selectedRecord(record);
              },
            })}
            columns={columns}
            rowKey={(record) => record.gid}
            dataSource={events}
            pagination={pagination}
            onChange={handleTableChange}
          />
        )}
        permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]}
      >
        <Table
          onRow={(record) => ({
            onClick: () => {
              selectedRecord(record);
            },
          })}
          rowClassName={() => 'ant-table-clickable-row'}
          columns={[...columns, actionColumn]}
          rowKey={(record) => record.gid}
          dataSource={events}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </RBAC>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  events: state.eventPages.content,
  totalElements: state.eventPages.totalElements,
  loading: state.loading.loading,
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
  selectedAccount: state.account.selectedAccount,
  isSuperUser: state.user.superUser,
  preserveFilters: state.filterParams.preserveFilters,
  isMobileView: state.mobileView.isMobileView,
});

const mapDispatchToProps = (dispatch) => ({
  fetchEventPages: param => dispatch(eventPageActions.fetchEventPages({
    payload: param,
  })),
  clearEventDetails: param => dispatch(eventDetailsAction.clearEventDetails({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(EventsList);
